import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import {
  IonButton,
  IonIcon,
  IonDatetime,
  IonDatetimeButton,
  IonModal,
  IonTextarea,
} from '@ionic/react'
import {
  closeCircleOutline,
  checkmarkCircleOutline,
  calendarOutline,
} from 'ionicons/icons'
import { format, formatISO } from 'date-fns'
import { workOrder as workOrderType } from 'types'
import { generateInputErrorId, useSubmitMessages } from 'utils'
import InputError from 'components/formik/InputError'
import { Modal } from 'components'
import DeclineForm from './DeclineForm'

const ETA_DATETIME_ID = 'accept-decline-eta-datetime'

const TIME_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
]

const propTypes = {
  workOrder: workOrderType,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
}
const defaultProps = {}

const validationSchema = Yup.object({
  eta_date: Yup.string().required('Please enter your estimated arrival date'),
  eta_time: Yup.string().required('Please select a time of day'),
  tech_comments: Yup.string(),
})

function getInitialValues() {
  return {
    eta_date: formatISO(new Date(), { representation: 'date' }),
    eta_time: 'morning',
    tech_comments: '',
  }
}

function formatSlaDate(slaDate) {
  if (!slaDate) return null
  const [y, m, d] = slaDate.split('-').map(Number)
  return format(new Date(y, m - 1, d), 'MMMM do, yyyy')
}

function AcceptDeclineForm({ workOrder, onAccept, onDecline }) {
  const formikRef = useRef(null)
  const { displaySubmitFailure } = useSubmitMessages()
  const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const slaDateFormatted = formatSlaDate(workOrder?.slaDate)

  const closeDeclineModal = () => setShowDeclineModal(false)

  return (
    <>
      <Formik
        innerRef={formikRef}
        key={workOrder?.id}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          try {
            await onAccept(values)
            formikRef.current?.resetForm({
              values: getInitialValues(),
            })
          } catch (error) {
            displaySubmitFailure(error?.errors, null, error)
          }
        }}
        validateOnBlur={false}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit,
          validateForm,
          errors,
          touched,
        }) => {
          const etaDateFormatted = formatSlaDate(values.eta_date)
          const isNotRecalled =
            workOrder?.recalled == null || workOrder?.recalled === false
          const isLateETA =
            isNotRecalled &&
            workOrder?.slaDate &&
            values.eta_date > workOrder.slaDate

          return (
            <>
              <Form className="accept-decline-section">
                <h3 className="accept-decline-section__title">
                  Accept or decline work order
                </h3>
                {/* Custom IonDatetimeButton + IonDatetime used instead of DateButton so we can keep this section's specific styling (e.g. full-width trigger, calendar icon). */}
                <div className="accept-decline-section__field">
                  <label
                    className="accept-decline-section__label"
                    htmlFor={ETA_DATETIME_ID}
                  >
                    <span className="required-indicator" aria-hidden="true">
                      *
                    </span>
                    Please enter your estimated arrival date
                  </label>
                  <div className="accept-decline-section__datetime-wrapper">
                    <IonDatetimeButton
                      datetime={ETA_DATETIME_ID}
                      className="accept-decline-section__datetime-trigger"
                      aria-required="true"
                      aria-describedby={
                        errors.eta_date && touched.eta_date
                          ? generateInputErrorId('eta_date')
                          : undefined
                      }
                    />
                    <IonIcon
                      icon={calendarOutline}
                      className="accept-decline-section__datetime-icon"
                      aria-hidden="true"
                    />
                    <IonModal keepContentsMounted={true}>
                      <IonDatetime
                        id={ETA_DATETIME_ID}
                        presentation="date"
                        value={values.eta_date}
                        showDefaultButtons={true}
                        onIonChange={(e) => {
                          const v = e.detail.value
                          const dateValue = v
                            ? v.slice(0, 10)
                            : formatISO(new Date(), { representation: 'date' })
                          setFieldValue('eta_date', dateValue)
                          setFieldTouched('eta_date', true)
                        }}
                      />
                    </IonModal>
                  </div>
                  <InputError
                    name="eta_date"
                    error={errors.eta_date}
                    touched={touched.eta_date}
                  />
                </div>
                {slaDateFormatted && isNotRecalled && (
                  <p className="accept-decline-section__sla">
                    Please try to be onsite by{' '}
                    <strong>{slaDateFormatted}</strong>.
                  </p>
                )}
                {/* Native select used instead of IonSelect: manager portal is viewed on laptops primarily and IonSelect looks strange on desktop. */}
                <div className="accept-decline-section__field">
                  <label
                    className="accept-decline-section__label"
                    htmlFor="accept-decline-time-of-day"
                  >
                    <span className="required-indicator" aria-hidden="true">
                      *
                    </span>
                    Please enter the relative time of day for your estimated
                    arrival date
                  </label>
                  <select
                    id="accept-decline-time-of-day"
                    name="eta_time"
                    className="accept-decline-section__input-native"
                    value={values.eta_time}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-required="true"
                    aria-describedby={
                      errors.eta_time && touched.eta_time
                        ? generateInputErrorId('eta_time')
                        : undefined
                    }
                  >
                    {TIME_OPTIONS.map((opt) => (
                      <option value={opt.value} key={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <InputError
                    name="eta_time"
                    error={errors.eta_time}
                    touched={touched.eta_time}
                  />
                </div>
                <p className="accept-decline-section__decline-note">
                  If you select to decline this Work Order, you'll be asked to
                  enter a brief reason on the next screen.
                </p>
                <div className="accept-decline-section__field">
                  <label
                    className="accept-decline-section__label"
                    htmlFor="accept-decline-tech-comments"
                  >
                    Tech Comments
                  </label>
                  <IonTextarea
                    id="accept-decline-tech-comments"
                    name="tech_comments"
                    className="accept-decline-section__input-native accept-decline-section__textarea"
                    value={values.tech_comments}
                    onIonChange={(e) =>
                      setFieldValue('tech_comments', e.detail.value ?? '')
                    }
                    onIonBlur={() => setFieldTouched('tech_comments', true)}
                    placeholder=""
                    rows={4}
                    aria-describedby={
                      errors.tech_comments && touched.tech_comments
                        ? generateInputErrorId('tech_comments')
                        : undefined
                    }
                  />
                </div>
                <div className="accept-decline-section__hint-row">
                  <p className="accept-decline-section__hint">
                    Please select if you'll accept or decline this work order
                  </p>
                  <div className="accept-decline-section__actions">
                    <IonButton
                      type="button"
                      fill="outline"
                      color="medium"
                      className="accept-decline-section__btn"
                      aria-label="Decline work order"
                      onClick={() => setShowDeclineModal(true)}
                    >
                      <IonIcon
                        icon={closeCircleOutline}
                        slot="start"
                        aria-hidden="true"
                      />
                      Decline
                    </IonButton>
                    <IonButton
                      type="button"
                      color="tss-grey-dark"
                      className="accept-decline-section__btn"
                      aria-label="Accept work order"
                      onClick={async () => {
                        const errs = await validateForm()
                        if (isEmpty(errs)) setShowAcceptConfirmModal(true)
                      }}
                    >
                      <IonIcon icon={checkmarkCircleOutline} slot="start" />
                      Accept
                    </IonButton>
                  </div>
                </div>
              </Form>

              <Modal
                isOpen={showAcceptConfirmModal}
                dismissModal={() => setShowAcceptConfirmModal(false)}
                header={
                  isLateETA
                    ? 'Are you sure you want to accept?'
                    : 'Confirm accept'
                }
                fitContent
                className="accept-or-decline-modal"
              >
                <div
                  className={classNames('accept-or-decline-modal__body', {
                    'accept-or-decline-modal__body--warning': isLateETA,
                  })}
                >
                  {isLateETA ? (
                    <p>
                      Please confirm that you are accepting this work order and
                      that we'll see you on {etaDateFormatted}. If you can,
                      please try to get here by {slaDateFormatted}.
                    </p>
                  ) : (
                    <p>
                      Please confirm that you are accepting this work order and
                      that we'll see you on {etaDateFormatted}.
                    </p>
                  )}
                </div>
                <div className="accept-or-decline-modal__actions">
                  <IonButton
                    type="button"
                    fill="outline"
                    color="medium"
                    aria-label="Cancel and close confirmation"
                    onClick={() => setShowAcceptConfirmModal(false)}
                  >
                    Cancel
                  </IonButton>
                  <IonButton
                    type="button"
                    color="tss-grey-dark"
                    aria-label="Confirm accept work order"
                    onClick={() => {
                      setShowAcceptConfirmModal(false)
                      handleSubmit()
                    }}
                  >
                    Confirm
                  </IonButton>
                </div>
              </Modal>
            </>
          )
        }}
      </Formik>

      <Modal
        isOpen={showDeclineModal}
        dismissModal={closeDeclineModal}
        header="Decline"
        fitContent
        className="accept-or-decline-modal"
      >
        <DeclineForm
          onCancel={closeDeclineModal}
          onSubmit={async (values) => {
            try {
              await onDecline(values)
              closeDeclineModal()
              formikRef.current?.resetForm({
                values: getInitialValues(),
              })
            } catch (error) {
              displaySubmitFailure(error?.errors, null, error)
            }
          }}
        />
      </Modal>
    </>
  )
}

AcceptDeclineForm.propTypes = propTypes
AcceptDeclineForm.defaultProps = defaultProps

export default AcceptDeclineForm
